class GameUI {
    constructor(game) {
        this.game = game;
        this.taskElements = new Map();
        this.upgradeElements = new Map();
        this.dialogueVisible = false;
        this.gameCompleteShown = false;
        this.lastGameState = {
            money: 0,
            reputation: 0,
            workProgress: 0,
            currentTask: null,
            unlockedTasks: [],
            unlockedUpgrades: [],
            dialogue: null,
            dialogueHandled: false,
            // Extended state tracking for better change detection
            tasksCompleted: 0,
            coffeeTaken: 0,
            productivityMultiplier: 1,
            achievementsCount: 0,
            influence: 0,
            autoClickerCps: 0,
            prestigeLevel: 0,
            employeeCount: 0,
            politicsEnabled: false,
            currentDilemma: null,
            dilemmaTimer: 0,
            politicsGameOver: false
        }; // Initialize with default values

        // Cached DOM references for stats (initialized in initializeUI)
        this.statElements = {};

        this.initializeUI();
    }

    // Efficient array comparison without JSON.stringify
    arraysEqual(a, b) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    // Helper to add both click and touch event handlers for better mobile support
    // This prevents the 300ms touch delay on mobile devices
    addTouchClickHandler(element, handler, options = {}) {
        let touchHandled = false;

        // Touch handler - fires immediately on touch devices
        element.addEventListener('touchend', (e) => {
            e.preventDefault(); // Prevent subsequent click event
            touchHandled = true;
            handler(e);
            // Reset after a short delay
            setTimeout(() => { touchHandled = false; }, 300);
        }, { passive: false, ...options });

        // Click handler - fires on desktop or as fallback
        element.addEventListener('click', (e) => {
            if (!touchHandled) {
                handler(e);
            }
        }, { passive: true, ...options });
    }

    getGameState() {
        return {
            money: Math.floor(this.game.resources?.money || 0),
            reputation: Math.floor(this.game.resources?.reputation || 0),
            workProgress: this.game.resources?.workInProgress || 0,
            currentTask: this.game.currentTask?.id,
            unlockedTasks: this.game.tasks?.filter(t => !t.locked).map(t => t.id) || [],
            unlockedUpgrades: this.game.upgrades?.filter(u => !u.locked).map(u => u.id) || [],
            dialogue: this.game.currentDialogue,
            dialogueHandled: this.game.dialogueHandled,
            // Extended state for change detection
            tasksCompleted: this.game.stats?.tasksCompleted || 0,
            coffeeTaken: this.game.stats?.coffeeTaken || 0,
            productivityMultiplier: this.game.productivityMultiplier || 1,
            achievementsCount: this.game.achievements?.size || 0,
            influence: Math.floor(this.game.resources?.influence || 0),
            autoClickerCps: this.game.autoClicker?.clicksPerSecond || 0,
            prestigeLevel: this.game.prestige?.level || 0,
            employeeCount: this.game.employees?.length || 0,
            politicsEnabled: this.game.politics?.enabled || false,
            currentDilemma: this.game.politics?.currentDilemma?.id || null,
            dilemmaTimer: Math.ceil(this.game.politics?.dilemmaTimer || 0),
            politicsGameOver: this.game.politics?.gameOver || false
        };
    }

    initializeUI() {
        document.body.innerHTML = `
            <div class="game-container">
                <div id="main-menu" class="hidden">
                    <div class="menu-content">
                        <h1>Office Worker Idle</h1>
                        <p>Your first week at the new job</p>
                        <div class="menu-buttons">
                            <button id="continue-btn" class="menu-button hidden">Continue</button>
                            <button id="new-game-btn" class="menu-button">New Game</button>
                        </div>
                        <div id="save-info" class="save-info hidden"></div>
                    </div>
                </div>

                <div id="game-area" class="hidden">
                    <!-- Left Sidebar: Dynamic elements that appear/disappear -->
                    <div class="left-sidebar">
                        <div id="dialogue-box" class="hidden">
                            <div class="dialogue-header">
                                <div class="portrait-circle"></div>
                                <div class="character-name"></div>
                            </div>
                            <div class="dialogue-content">
                                <p class="dialogue-text"></p>
                                <div class="dialogue-choices"></div>
                            </div>
                        </div>

                        <div id="work-area" class="hidden">
                            <div class="current-task-info">
                                <span id="current-task-name">No task selected</span>
                                <div class="work-progress-bar">
                                    <div id="work-progress-fill" class="work-progress-fill"></div>
                                </div>
                                <span id="work-progress-text">0%</span>
                            </div>
                            <button id="work-btn" class="work-button">Work!</button>
                        </div>
                    </div>

                    <!-- Center: Static content that stays in place -->
                    <div class="center-content">
                        <div class="header">
                            <div class="resources">
                                Money: $<span id="money">0</span> |
                                Rep: <span id="rep">0</span>
                                <span id="influence-display" class="hidden"> | Influence: <span id="influence">0</span></span>
                                <span id="auto-clicker-display" class="hidden"> | Auto: <span id="auto-clicks">0</span>/s</span>
                                <span id="save-indicator" class="save-indicator hidden">Saved!</span>
                            </div>
                        </div>

                        <div id="tutorial-panel" class="tutorial-panel hidden">
                            <div class="tutorial-header">
                                <h4>How to Play</h4>
                                <button id="close-tutorial" class="close-tutorial">Got it!</button>
                            </div>
                            <ul class="tutorial-list">
                                <li><strong>Dialogues:</strong> Make choices to build relationships</li>
                                <li><strong>Tasks:</strong> Click "Start" then click "Work!" to complete</li>
                                <li><strong>Upgrades:</strong> Spend money on coffee breaks for speed boosts</li>
                                <li><strong>Goal:</strong> Complete your first week at the office!</li>
                            </ul>
                        </div>

                        <div class="main-content">
                            <div class="tasks-panel">
                                <h4>Available Tasks</h4>
                                <div id="tasks"></div>
                            </div>

                            <div class="upgrades-panel">
                                <h4>Actions</h4>
                                <div id="upgrades"></div>
                            </div>

                            <div id="automation-panel" class="automation-panel hidden">
                                <h4>Automation Controls</h4>
                                <div id="automation-toggles"></div>
                            </div>

                            <div id="employees-panel" class="employees-panel hidden">
                                <h4>Team</h4>
                                <div id="employee-hire-buttons"></div>
                                <div id="employee-list"></div>
                            </div>

                            <div id="career-panel" class="career-panel hidden">
                                <h4>Career</h4>
                                <div class="career-info">
                                    <div class="title-display">
                                        <span class="label">Title:</span>
                                        <span id="current-title" class="value">New Hire</span>
                                    </div>
                                    <div class="promotion-progress">
                                        <span class="label">Next Promotion:</span>
                                        <span id="promotion-requirement" class="value">$100</span>
                                    </div>
                                    <div class="bonuses-display">
                                        <small id="permanent-bonuses"></small>
                                    </div>
                                    <button id="promote-btn" class="promote-button" disabled>Get Promoted</button>
                                </div>
                            </div>

                            <div id="politics-panel" class="politics-panel hidden">
                                <h4>Office Politics</h4>
                                <div class="factions-display" id="factions-display"></div>
                                <div id="dilemma-box" class="dilemma-box hidden">
                                    <div class="dilemma-header">Political Dilemma</div>
                                    <p class="dilemma-text" id="dilemma-text"></p>
                                    <div class="dilemma-choices">
                                        <button class="dilemma-choice" id="dilemma-choice-a"></button>
                                        <button class="dilemma-choice" id="dilemma-choice-b"></button>
                                    </div>
                                </div>
                                <div id="dilemma-timer" class="dilemma-timer hidden">
                                    Next dilemma in: <span id="dilemma-countdown">30</span>s
                                </div>
                                <div class="schemes-list" id="schemes-list"></div>
                            </div>

                            <div id="politics-gameover" class="politics-gameover hidden">
                                <h3>Career Over</h3>
                                <p id="gameover-reason"></p>
                                <button id="politics-restart-btn" class="play-again-button">Try Again</button>
                            </div>
                        </div>
                    </div>

                    <!-- Right Sidebar: Stats (collapsible) -->
                    <div class="right-sidebar">
                        <div id="stats-panel" class="stats-panel">
                            <div class="stats-header" id="stats-toggle" tabindex="0" role="button" aria-expanded="true">
                                <h4>Statistics</h4>
                                <span class="toggle-icon">▼</span>
                            </div>
                            <div class="stats-content" id="stats-content">
                                <div class="stats-grid">
                                    <div class="stat-item">
                                        <span class="stat-label">Tasks Completed</span>
                                        <span class="stat-value" id="stat-tasks">0</span>
                                    </div>
                                    <div class="stat-item">
                                        <span class="stat-label">Total Earned</span>
                                        <span class="stat-value" id="stat-earned">$0</span>
                                    </div>
                                    <div class="stat-item">
                                        <span class="stat-label">Reputation</span>
                                        <span class="stat-value" id="stat-rep">0</span>
                                    </div>
                                    <div class="stat-item">
                                        <span class="stat-label">Achievements</span>
                                        <span class="stat-value" id="stat-achievements">0/0</span>
                                    </div>
                                    <div class="stat-item">
                                        <span class="stat-label">Coffee Breaks</span>
                                        <span class="stat-value" id="stat-coffee">0</span>
                                    </div>
                                    <div class="stat-item">
                                        <span class="stat-label">Productivity</span>
                                        <span class="stat-value" id="stat-productivity">100%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="game-complete" class="hidden">
                    <div class="complete-content">
                        <h2>Congratulations!</h2>
                        <p>You've completed your first week at the office.</p>
                        <div class="final-stats">
                            <h3>Final Stats</h3>
                            <div id="final-money"></div>
                            <div id="final-reputation"></div>
                            <div id="final-tasks"></div>
                            <div id="final-achievements"></div>
                        </div>
                        <div class="relationships-summary">
                            <h3>Relationships</h3>
                            <div id="final-relationships"></div>
                        </div>
                        <button id="play-again-btn" class="play-again-button">Play Again</button>
                    </div>
                </div>

                <div id="about-footer" class="about-footer">
                    <div id="about-popup" class="about-popup hidden">
                        <div class="about-title">Office Worker Idle</div>
                        <p class="about-description">
                            A narrative-driven idle game about surviving your first
                            week at a new office job. Make choices, build relationships,
                            and climb the corporate ladder.
                        </p>
                        <p class="about-credit">Created by Charles Lukowski</p>
                        <div class="about-links">
                            <a href="https://charleslukowski.com" target="_blank" rel="noopener noreferrer">charleslukowski.com</a>
                            <span>&middot;</span>
                            <a href="https://twitter.com/chux52" target="_blank" rel="noopener noreferrer">@chux52</a>
                        </div>
                    </div>
                    <button id="about-btn" class="about-button" aria-label="About this game">About</button>
                </div>
            </div>
        `;

        const style = document.createElement('style');
        style.textContent = `
            /* ===========================================
               Minimalist UI - Inspired by A Dark Room
               Typography: 14px base, 16px headings
               Colors: #333 primary, #666 secondary, #ddd borders
               Accent: #4a7c59 (muted green)
               =========================================== */

            * {
                box-sizing: border-box;
                touch-action: manipulation;
            }

            .game-container {
                font-family: "Courier New", Courier, monospace;
                font-size: 14px;
                color: #333;
                max-width: 1200px;
                margin: 20px auto;
                padding: 15px;
                line-height: 1.5;
            }

            /* --- 3-Column Layout: sidebars for dynamic, center for static --- */
            #game-area {
                display: grid;
                grid-template-columns: 200px 1fr 200px;
                gap: 20px;
                align-items: start;
            }

            .left-sidebar, .right-sidebar {
                position: sticky;
                top: 20px;
            }

            .left-sidebar {
                min-height: 50px;
            }

            .center-content {
                min-width: 0;
            }

            h1, h2, h3, h4 {
                font-size: 16px;
                font-weight: bold;
                margin: 0 0 10px 0;
            }

            /* --- Buttons: Uniform styling --- */
            button {
                font-family: inherit;
                font-size: 14px;
                padding: 6px 12px;
                background: transparent;
                border: 1px solid #333;
                cursor: pointer;
                color: #333;
            }
            button:hover {
                text-decoration: underline;
            }
            button:focus-visible {
                outline: 2px solid #4a7c59;
                outline-offset: 2px;
            }
            button:disabled {
                color: #999;
                border-color: #ccc;
                cursor: default;
                text-decoration: none;
            }

            .hidden {
                display: none !important;
            }

            /* --- Header --- */
            .header {
                border-bottom: 1px solid #ddd;
                padding: 10px 0;
                margin-bottom: 15px;
            }
            .resources {
                font-size: 14px;
            }

            /* --- Dialogue (in left sidebar) --- */
            #dialogue-box {
                border: 1px solid #333;
                margin-bottom: 15px;
                padding: 12px;
            }
            .dialogue-header {
                display: flex;
                align-items: center;
                margin-bottom: 8px;
            }
            .portrait-circle {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                background: #ddd;
                margin-right: 8px;
                border: 1px solid #999;
                flex-shrink: 0;
            }
            .character-name {
                font-weight: bold;
                font-size: 13px;
            }
            .dialogue-text {
                margin: 0 0 12px 0;
                font-size: 12px;
                line-height: 1.5;
            }
            .dialogue-choices {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .choice-btn {
                text-align: left;
                padding: 8px 10px;
                font-size: 12px;
            }

            /* --- Work Area (in left sidebar) --- */
            #work-area {
                border: 1px solid #333;
                padding: 12px;
                margin-bottom: 15px;
                text-align: center;
            }
            .current-task-info {
                margin-bottom: 10px;
            }
            #current-task-name {
                font-weight: bold;
                display: block;
                margin-bottom: 8px;
                font-size: 12px;
                line-height: 1.4;
            }
            .work-progress-bar {
                height: 8px;
                background: #eee;
                border: 1px solid #ddd;
                margin: 8px 0;
            }
            .work-progress-fill {
                height: 100%;
                background: #4a7c59;
                width: 0%;
            }
            #work-progress-text {
                color: #666;
            }
            .work-button {
                padding: 10px 20px;
                font-weight: bold;
                background: #4a7c59;
                color: white;
                border: 1px solid #3d6349;
                width: 100%;
            }
            .work-button:hover {
                background: #3d6349;
                text-decoration: none;
            }
            .work-button:focus-visible {
                outline: 2px solid #333;
                outline-offset: 2px;
                box-shadow: 0 0 0 4px rgba(74, 124, 89, 0.3);
            }
            .work-button:disabled {
                background: #999;
                border-color: #999;
            }

            /* --- Main Content Grid --- */
            .main-content {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
            }
            .tasks-panel, .upgrades-panel {
                min-height: 100px;
            }

            /* --- Cards (Tasks & Upgrades) --- */
            .card {
                border: 1px solid #ddd;
                padding: 10px;
                margin: 6px 0;
                background: white;
            }
            .card.active {
                border-color: #333;
                background: #f9f9f9;
            }
            .task-header, .upgrade-content {
                margin-bottom: 8px;
            }
            small {
                color: #666;
                display: block;
                margin-top: 4px;
            }

            /* --- Progress Bars (uniform) --- */
            .progress-bar {
                height: 6px;
                background: #eee;
                margin-top: 8px;
                border: 1px solid #ddd;
            }
            .progress-bar-fill {
                height: 100%;
                background: #4a7c59;
                width: 0%;
            }
            .timer-bar {
                height: 4px;
                background: #eee;
                margin-top: 6px;
            }
            .timer-fill {
                height: 100%;
                background: #666;
                width: 0%;
            }

            /* --- Tutorial --- */
            .tutorial-panel {
                border: 1px solid #333;
                padding: 15px;
                margin-bottom: 15px;
            }
            .tutorial-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;
            }
            .tutorial-header h4 {
                margin: 0;
            }
            .close-tutorial {
                padding: 4px 10px;
            }
            .tutorial-list {
                margin: 0;
                padding-left: 20px;
            }
            .tutorial-list li {
                margin: 6px 0;
            }

            /* --- Stats Panel (in right sidebar) --- */
            .stats-panel {
                border: 1px solid #ddd;
            }
            .stats-header {
                background: #f5f5f5;
                padding: 10px;
                cursor: pointer;
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid #ddd;
            }
            .stats-header:hover {
                background: #eee;
            }
            .stats-header:focus-visible {
                outline: 2px solid #4a7c59;
                outline-offset: -2px;
            }
            .stats-header h4 {
                margin: 0;
            }
            .toggle-icon {
                font-size: 12px;
            }
            .stats-panel.collapsed .toggle-icon {
                transform: rotate(-90deg);
            }
            .stats-panel.collapsed .stats-content {
                display: none;
            }
            .stats-content {
                padding: 10px;
            }
            .stats-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 8px;
            }
            .stat-item {
                padding: 8px;
                text-align: center;
                border: 1px solid #eee;
            }
            .stat-label {
                display: block;
                color: #666;
                margin-bottom: 4px;
                font-size: 11px;
            }
            .stat-value {
                display: block;
                font-weight: bold;
            }

            /* --- Menu --- */
            #main-menu {
                text-align: center;
                padding: 60px 20px;
            }
            .menu-content p {
                color: #666;
                margin-bottom: 30px;
            }
            .menu-buttons {
                display: flex;
                flex-direction: column;
                gap: 10px;
                align-items: center;
            }
            .menu-button {
                min-width: 180px;
                padding: 10px 30px;
            }
            .save-info {
                margin-top: 20px;
                color: #666;
            }
            .save-indicator {
                color: #4a7c59;
                margin-left: 10px;
                opacity: 0;
            }
            .save-indicator.show {
                opacity: 1;
            }

            /* --- Automation Controls Panel --- */
            .automation-panel {
                border: 1px solid #ddd;
                padding: 10px;
                margin-top: 15px;
                background: #f9f9f9;
            }
            .automation-panel h4 {
                margin-bottom: 10px;
                font-size: 14px;
            }
            .automation-toggle {
                margin: 8px 0;
            }
            .toggle-label {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            .toggle-name {
                font-size: 13px;
            }
            .toggle-btn {
                padding: 4px 12px;
                font-size: 11px;
                min-width: 40px;
                border: 1px solid #999;
                background: #ddd;
                color: #666;
                cursor: pointer;
                border-radius: 3px;
            }
            .toggle-btn.active {
                background: #4a7c59;
                border-color: #3d6349;
                color: white;
            }
            .toggle-btn:hover {
                opacity: 0.9;
            }

            /* --- Employees Panel --- */
            .employees-panel {
                border: 1px solid #ddd;
                padding: 10px;
                margin-top: 15px;
            }
            .employees-panel h4 {
                margin-bottom: 10px;
            }
            #employee-hire-buttons {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                margin-bottom: 10px;
            }
            .hire-btn {
                padding: 6px 10px;
                font-size: 12px;
            }
            .hire-btn:disabled {
                opacity: 0.5;
            }
            #employee-list {
                font-size: 12px;
            }
            .employee-item {
                padding: 6px;
                border: 1px solid #eee;
                margin: 4px 0;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            .employee-item .employee-task {
                color: #666;
                font-size: 11px;
            }
            .employee-progress {
                height: 4px;
                background: #eee;
                margin-top: 4px;
            }
            .employee-progress-fill {
                height: 100%;
                background: #4a7c59;
                width: 0%;
            }

            /* --- Career Panel --- */
            .career-panel {
                border: 1px solid #ddd;
                padding: 10px;
                margin-top: 15px;
            }
            .career-info {
                font-size: 13px;
            }
            .career-info .label {
                color: #666;
            }
            .career-info .value {
                font-weight: bold;
            }
            .title-display, .promotion-progress {
                margin-bottom: 8px;
            }
            .bonuses-display {
                margin-bottom: 10px;
                color: #4a7c59;
            }
            .promote-button {
                width: 100%;
                padding: 8px;
                background: #4a7c59;
                color: white;
                border: 1px solid #3d6349;
            }
            .promote-button:hover:not(:disabled) {
                background: #3d6349;
            }
            .promote-button:focus-visible {
                outline: 2px solid #333;
                outline-offset: 2px;
                box-shadow: 0 0 0 4px rgba(74, 124, 89, 0.3);
            }
            .promote-button:disabled {
                background: #999;
                border-color: #999;
            }

            /* --- Politics Panel --- */
            .politics-panel {
                border: 1px solid #ddd;
                padding: 10px;
                margin-top: 15px;
            }
            .factions-display {
                display: flex;
                gap: 10px;
                margin-bottom: 10px;
                flex-wrap: wrap;
            }
            .faction-item {
                flex: 1;
                min-width: 80px;
                padding: 8px;
                text-align: center;
                border: 1px solid #ddd;
                font-size: 11px;
            }
            .faction-item .faction-name {
                font-weight: bold;
                display: block;
                margin-bottom: 4px;
            }
            .faction-item .faction-standing {
                color: #666;
            }
            .faction-item.allied { border-color: #4a7c59; background: #f0f7f2; }
            .faction-item.friendly { border-color: #7cb342; }
            .faction-item.wary { border-color: #ff9800; background: #fff8e1; }
            .faction-item.hostile { border-color: #d32f2f; background: #ffeaea; }

            /* Faction favor bar */
            .faction-favor-bar {
                height: 6px;
                background: #eee;
                margin-top: 4px;
                border-radius: 3px;
                overflow: hidden;
            }
            .faction-favor-fill {
                height: 100%;
                transition: width 0.3s ease;
            }
            .faction-favor-fill.high { background: #4a7c59; }
            .faction-favor-fill.medium { background: #7cb342; }
            .faction-favor-fill.low { background: #ff9800; }
            .faction-favor-fill.danger { background: #d32f2f; }

            /* Faction Warning */
            .faction-warning {
                background: #fff3cd;
                border: 2px solid #ff9800;
                color: #856404;
                padding: 8px 12px;
                margin: 8px 0;
                font-size: 12px;
                font-weight: bold;
                animation: warningPulse 2s ease-in-out infinite;
            }
            @keyframes warningPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
            }

            /* Political Legacy (end screen) */
            .final-politics-section {
                margin-top: 12px;
                padding-top: 10px;
                border-top: 1px solid #ccc;
            }
            .final-politics-section h3 {
                margin: 0 0 8px 0;
                font-size: 14px;
                color: #333;
            }
            .final-politics-section div {
                font-size: 12px;
                margin: 3px 0;
            }

            /* Dilemma Box */
            .dilemma-box {
                border: 2px solid #333;
                padding: 15px;
                margin: 15px 0;
                background: #fffef5;
            }
            .dilemma-header {
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 10px;
                text-align: center;
                color: #8b4513;
            }
            .dilemma-text {
                font-size: 13px;
                line-height: 1.5;
                margin-bottom: 15px;
            }
            .dilemma-choices {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
            .dilemma-choice {
                padding: 12px 15px;
                text-align: left;
                font-size: 13px;
                background: white;
                border: 1px solid #333;
                cursor: pointer;
                transition: background 0.2s;
            }
            .dilemma-choice:hover {
                background: #f0f0f0;
            }
            .dilemma-choice:focus {
                outline: 2px solid #4a7c59;
                outline-offset: 2px;
            }
            .dilemma-choice-text {
                display: block;
                margin-bottom: 6px;
            }
            .dilemma-effects {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
                font-size: 11px;
            }
            .effect-indicator {
                padding: 2px 6px;
                border-radius: 3px;
                font-weight: bold;
            }
            .effect-indicator.positive {
                background: #d4edda;
                color: #155724;
            }
            .effect-indicator.negative {
                background: #f8d7da;
                color: #721c24;
            }

            .dilemma-timer {
                font-size: 12px;
                color: #666;
                text-align: center;
                padding: 8px;
            }

            /* Politics Game Over */
            .politics-gameover {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.8);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                z-index: 1000;
                color: white;
                text-align: center;
                padding: 20px;
            }
            .politics-gameover h3 {
                font-size: 24px;
                color: #ff6b6b;
                margin-bottom: 20px;
            }
            .politics-gameover p {
                font-size: 16px;
                max-width: 400px;
                margin-bottom: 30px;
                line-height: 1.6;
            }

            .schemes-list {
                font-size: 12px;
            }
            .scheme-item {
                padding: 8px;
                border: 1px solid #eee;
                margin: 4px 0;
            }
            .scheme-item.completed {
                opacity: 0.5;
                background: #f5f5f5;
            }
            .scheme-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 4px;
            }
            .scheme-name {
                font-weight: bold;
            }
            .scheme-cost {
                color: #666;
            }
            .scheme-description {
                color: #666;
                font-size: 11px;
                margin-bottom: 6px;
            }
            .scheme-btn {
                padding: 4px 10px;
                font-size: 11px;
            }

            /* --- Game Complete --- */
            #game-complete {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.7);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 2000; /* Higher than politics-gameover to ensure victory shows */
            }
            .complete-content {
                background: white;
                padding: 30px;
                max-width: 450px;
                text-align: center;
                border: 1px solid #333;
            }
            .complete-content h2 {
                margin-bottom: 10px;
            }
            .final-stats, .relationships-summary {
                border: 1px solid #ddd;
                padding: 15px;
                margin: 15px 0;
                text-align: left;
            }
            .final-stats h3, .relationships-summary h3 {
                margin: 0 0 10px 0;
            }
            .final-stats div, .relationships-summary div {
                margin: 4px 0;
            }
            .play-again-button {
                margin-top: 15px;
                padding: 10px 30px;
            }

            /* --- Notifications (static, no animation) --- */
            .notification {
                position: fixed;
                top: 20px;
                right: 20px;
                background: white;
                border: 1px solid #333;
                padding: 10px 15px;
                z-index: 1000;
            }

            /* --- Responsive: Tablet (collapse to 2 columns) --- */
            @media (max-width: 900px) {
                #game-area {
                    grid-template-columns: 180px 1fr 180px;
                    gap: 15px;
                }

                .game-container {
                    margin: 10px auto;
                    padding: 12px;
                }

                .complete-content {
                    max-width: 90%;
                    padding: 20px;
                }
            }

            /* --- Responsive: Mobile (single column, fixed work area) --- */
            @media (max-width: 700px) {
                .game-container {
                    margin: 0;
                    padding: 10px;
                    padding-bottom: 140px; /* Reserve space for fixed work area */
                    font-size: 15px;
                }

                /* Stack layout vertically */
                #game-area {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .left-sidebar, .right-sidebar {
                    position: static;
                    order: 0;
                }

                .center-content {
                    order: 1;
                }

                /* Dialogue stays at top when visible */
                .left-sidebar {
                    order: -1;
                }

                /* Stats panel inline on mobile */
                .right-sidebar {
                    order: 2;
                }

                .stats-grid {
                    grid-template-columns: repeat(3, 1fr);
                    gap: 8px;
                }

                .stat-label {
                    font-size: 11px;
                }

                /* Work area fixed at bottom - prevents jumping */
                #work-area {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    margin: 0;
                    padding: 12px 15px;
                    background: #fafafa;
                    border-top: 2px solid #333;
                    border-bottom: none;
                    border-left: none;
                    border-right: none;
                    z-index: 100;
                    box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
                }

                #work-area .current-task-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 0;
                }

                #work-area #current-task-name {
                    font-size: 14px;
                    margin-bottom: 0;
                    flex: 1;
                }

                #work-area .work-progress-bar {
                    flex: 1;
                    margin: 0;
                    height: 10px;
                }

                #work-area #work-progress-text {
                    min-width: 40px;
                    text-align: right;
                }

                #work-area .work-button {
                    width: auto;
                    min-width: 100px;
                    margin-left: 12px;
                }

                /* Dialogue larger on mobile - ensure it's above fixed work area */
                #dialogue-box {
                    padding: 15px;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 200;
                    background: white;
                    border: none;
                    border-bottom: 2px solid #333;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                    max-height: 60vh;
                    overflow-y: auto;
                }

                .portrait-circle {
                    width: 36px;
                    height: 36px;
                }

                .character-name {
                    font-size: 15px;
                }

                .dialogue-text {
                    font-size: 14px;
                }

                .choice-btn {
                    min-height: 48px;
                    padding: 12px 16px;
                    font-size: 15px;
                }

                h1, h2, h3, h4 {
                    font-size: 17px;
                }

                /* Touch-friendly buttons (44px min height) */
                button {
                    min-height: 44px;
                    padding: 10px 16px;
                    font-size: 15px;
                }

                /* Main content single column */
                .main-content {
                    grid-template-columns: 1fr;
                    gap: 12px;
                }

                /* Card spacing */
                .card {
                    padding: 12px;
                    margin: 8px 0;
                }

                /* Menu buttons */
                .menu-button {
                    min-width: 200px;
                    min-height: 48px;
                    padding: 12px 30px;
                }

                #main-menu {
                    padding: 40px 15px;
                }

                /* Tutorial list spacing */
                .tutorial-list li {
                    margin: 10px 0;
                    line-height: 1.6;
                }

                /* Progress bars slightly taller for visibility */
                .progress-bar {
                    height: 8px;
                }

                /* Header resources wrapping */
                .resources {
                    font-size: 15px;
                    line-height: 1.6;
                }

                /* Game complete modal */
                .complete-content {
                    max-width: 95%;
                    max-height: 90vh;
                    overflow-y: auto;
                    padding: 20px 15px;
                }

                .final-stats, .relationships-summary {
                    padding: 12px;
                    margin: 12px 0;
                }

                .play-again-button {
                    min-height: 48px;
                    width: 100%;
                    max-width: 250px;
                }

                /* Notifications positioned for mobile */
                .notification {
                    top: 10px;
                    right: 10px;
                    left: 10px;
                    text-align: center;
                }
            }

            /* --- Responsive: Small Mobile --- */
            @media (max-width: 400px) {
                .game-container {
                    padding: 8px;
                    padding-bottom: 130px;
                }

                .stats-grid {
                    grid-template-columns: 1fr 1fr;
                    gap: 6px;
                }

                .stat-item {
                    padding: 8px 4px;
                }

                .stat-label {
                    font-size: 10px;
                }

                .stat-value {
                    font-size: 13px;
                }

                .menu-button {
                    min-width: 100%;
                }

                .complete-content h2 {
                    font-size: 18px;
                }

                .tutorial-panel {
                    padding: 12px;
                }

                .tutorial-list {
                    padding-left: 16px;
                    font-size: 14px;
                }

                /* Compact work area on small screens */
                #work-area {
                    padding: 10px;
                }

                #work-area #current-task-name {
                    font-size: 12px;
                }

                #work-area .work-button {
                    min-width: 80px;
                    padding: 10px 15px;
                }
            }

            /* --- Touch device optimizations --- */
            @media (hover: none) and (pointer: coarse) {
                /* Remove hover underline on touch devices */
                button:hover {
                    text-decoration: none;
                }

                /* Active state for touch feedback */
                button:active {
                    opacity: 0.8;
                }

                .work-button:active {
                    background: #3d6349;
                }

                /* Larger tap targets for cards */
                .card button {
                    min-height: 44px;
                }

                /* Stats toggle easier to tap */
                .stats-header {
                    min-height: 44px;
                    padding: 12px;
                }
            }

            /* --- About Footer & Popup --- */
            .about-footer {
                position: relative;
                text-align: center;
                margin-top: 20px;
                padding: 10px 0;
            }

            .about-button {
                background: none;
                border: none;
                font-family: "Courier New", Courier, monospace;
                font-size: 12px;
                color: #999;
                cursor: pointer;
                padding: 4px 8px;
            }

            .about-button:hover {
                color: #666;
            }

            .about-popup {
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                margin-bottom: 8px;
                width: 280px;
                background: #fff;
                border: 1px solid #ddd;
                padding: 16px;
                box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
                z-index: 50;
                text-align: left;
            }

            .about-title {
                font-size: 14px;
                font-weight: bold;
                color: #333;
                margin-bottom: 8px;
            }

            .about-description {
                font-size: 12px;
                color: #666;
                line-height: 1.5;
                margin: 0 0 10px 0;
            }

            .about-credit {
                font-size: 12px;
                color: #666;
                margin: 0 0 10px 0;
            }

            .about-links {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 11px;
                flex-wrap: wrap;
            }

            .about-links a {
                color: #666;
                text-decoration: underline;
                text-decoration-color: #ccc;
            }

            .about-links a:hover {
                color: #333;
                text-decoration-color: #666;
            }

            .about-links span {
                color: #999;
            }

            @media (max-width: 700px) {
                .about-footer {
                    padding-bottom: 140px;
                }
            }
        `;
        document.head.appendChild(style);

        // Initialize menu elements
        this.mainMenu = document.getElementById('main-menu');
        this.gameArea = document.getElementById('game-area');
        this.continueBtn = document.getElementById('continue-btn');
        this.newGameBtn = document.getElementById('new-game-btn');
        this.saveInfo = document.getElementById('save-info');
        this.saveIndicator = document.getElementById('save-indicator');

        // Initialize game containers
        this.taskContainer = document.getElementById('tasks');
        this.upgradeContainer = document.getElementById('upgrades');
        this.dialogueBox = document.getElementById('dialogue-box');
        this.workArea = document.getElementById('work-area');
        this.workBtn = document.getElementById('work-btn');
        this.workProgressFill = document.getElementById('work-progress-fill');
        this.workProgressText = document.getElementById('work-progress-text');
        this.currentTaskName = document.getElementById('current-task-name');
        this.tutorialPanel = document.getElementById('tutorial-panel');
        this.closeTutorialBtn = document.getElementById('close-tutorial');

        // Tutorial close handler
        this.closeTutorialBtn.addEventListener('click', () => {
            this.tutorialPanel.classList.add('hidden');
            localStorage.setItem('tutorialSeen', 'true');
        });

        // Stats panel toggle
        this.statsPanel = document.getElementById('stats-panel');
        this.statsToggle = document.getElementById('stats-toggle');
        const toggleStats = () => {
            this.statsPanel.classList.toggle('collapsed');
            const isExpanded = !this.statsPanel.classList.contains('collapsed');
            this.statsToggle.setAttribute('aria-expanded', isExpanded);
        };
        this.statsToggle.addEventListener('click', toggleStats);
        this.statsToggle.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleStats();
            }
        });

        // Cache stat DOM elements for efficient updates
        this.statElements = {
            tasks: document.getElementById('stat-tasks'),
            earned: document.getElementById('stat-earned'),
            rep: document.getElementById('stat-rep'),
            achievements: document.getElementById('stat-achievements'),
            coffee: document.getElementById('stat-coffee'),
            productivity: document.getElementById('stat-productivity'),
            money: document.getElementById('money'),
            repHeader: document.getElementById('rep')
        };

        // Automation controls panel
        this.automationPanel = document.getElementById('automation-panel');
        this.automationToggles = document.getElementById('automation-toggles');
        this.setupAutomationControls();

        // New automation panels
        this.employeesPanel = document.getElementById('employees-panel');
        this.employeeHireButtons = document.getElementById('employee-hire-buttons');
        this.employeeList = document.getElementById('employee-list');

        this.careerPanel = document.getElementById('career-panel');
        this.currentTitleEl = document.getElementById('current-title');
        this.promotionReqEl = document.getElementById('promotion-requirement');
        this.permanentBonusesEl = document.getElementById('permanent-bonuses');
        this.promoteBtn = document.getElementById('promote-btn');

        this.politicsPanel = document.getElementById('politics-panel');
        this.factionsDisplay = document.getElementById('factions-display');
        this.schemesList = document.getElementById('schemes-list');

        // Dilemma elements
        this.dilemmaBox = document.getElementById('dilemma-box');
        this.dilemmaText = document.getElementById('dilemma-text');
        this.dilemmaChoiceA = document.getElementById('dilemma-choice-a');
        this.dilemmaChoiceB = document.getElementById('dilemma-choice-b');
        this.dilemmaTimer = document.getElementById('dilemma-timer');
        this.dilemmaCountdown = document.getElementById('dilemma-countdown');
        this.politicsGameover = document.getElementById('politics-gameover');
        this.gameoverReason = document.getElementById('gameover-reason');
        this.politicsRestartBtn = document.getElementById('politics-restart-btn');

        // Dilemma choice handlers
        this.dilemmaChoiceA.addEventListener('click', () => {
            if (this.game.makeDilemmaChoice('A')) {
                this.updatePoliticsPanel();
            }
        });
        this.dilemmaChoiceB.addEventListener('click', () => {
            if (this.game.makeDilemmaChoice('B')) {
                this.updatePoliticsPanel();
            }
        });

        // Politics restart handler
        this.politicsRestartBtn.addEventListener('click', () => {
            this.game.clearSaveData();
            location.reload();
        });

        this.influenceDisplay = document.getElementById('influence-display');
        this.influenceEl = document.getElementById('influence');
        this.autoClickerDisplay = document.getElementById('auto-clicker-display');
        this.autoClicksEl = document.getElementById('auto-clicks');

        // Promote button handler - using touch handler for better mobile support
        this.addTouchClickHandler(this.promoteBtn, () => {
            // Prevent double-clicks while processing
            if (this.promoteBtn.disabled) return;

            if (!this.game.canPromote()) {
                const nextReq = this.game.getNextPromotionRequirement();
                if (nextReq) {
                    const current = this.game.resources.money + this.game.stats.tasksCompleted * 10;
                    this.showNotification(`Need $${nextReq} total earned (currently $${current})`);
                }
                return;
            }

            this.promoteBtn.disabled = true;
            if (this.game.promote()) {
                // Don't show notification for CEO promotion - the victory dialogue handles that
                if (this.game.prestige.level < 10) {
                    this.showPromotionNotification(this.game.prestige.level);
                } else if (this.game.prestige.level === 10) {
                    // For CEO promotion, show the dialogue immediately instead of waiting for render loop
                    // This ensures the dialogue appears reliably on mobile devices
                    this.dialogueVisible = false; // Reset state to allow showing
                    if (this.game.currentDialogue) {
                        this.showDialogue(this.game.currentDialogue);
                    }
                }
                // Only call updateCareerPanel once to avoid race conditions
                this.updateCareerPanel();
            } else {
                // Re-enable button if promotion failed
                this.promoteBtn.disabled = false;
            }
        });

        // Track total money earned
        this.totalMoneyEarned = 0;

        // Setup menu button handlers
        this.continueBtn.addEventListener('click', () => {
            this.game.load();
            this.startGame(false); // Not a new game

            // On mobile, if loading a game at CEO level with pending victory dialogue,
            // show it immediately to avoid render loop timing issues
            if (this.game.prestige.level >= 10 && this.game.currentDialogue &&
                this.game.currentDialogue.id === 'ceo_victory') {
                this.dialogueVisible = false;
                this.showDialogue(this.game.currentDialogue);
            }
        });

        this.newGameBtn.addEventListener('click', () => {
            this.game.clearSaveData();
            location.reload();
        });

        // Work button handler - using touch handler for better mobile support
        this.addTouchClickHandler(this.workBtn, () => {
            this.game.click();
            this.updateWorkArea();
        });

        // Game completion screen elements
        this.gameCompleteScreen = document.getElementById('game-complete');
        this.playAgainBtn = document.getElementById('play-again-btn');

        // Play again handler - clears save and restarts
        this.playAgainBtn.addEventListener('click', () => {
            this.game.clearSaveData();
            location.reload();
        });

        // About popup
        this.aboutBtn = document.getElementById('about-btn');
        this.aboutPopup = document.getElementById('about-popup');
        this.aboutFooter = document.getElementById('about-footer');

        this.aboutBtn.addEventListener('click', () => {
            this.aboutPopup.classList.toggle('hidden');
        });

        // Close about popup on click outside
        document.addEventListener('mousedown', (e) => {
            if (!this.aboutPopup.classList.contains('hidden') &&
                !this.aboutFooter.contains(e.target)) {
                this.aboutPopup.classList.add('hidden');
            }
        });

        // Close about popup on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.aboutPopup.classList.contains('hidden')) {
                this.aboutPopup.classList.add('hidden');
            }
        });

        // Create task and upgrade elements BEFORE starting game
        // This ensures the elements exist when render() is called
        this.createTaskElements();
        this.createUpgradeElements();

        // Check for save data and show appropriate screen
        if (this.game.hasSaveData()) {
            this.showMainMenu(true);
        } else {
            this.startGame();
        }
    }

    showMainMenu(hasSave) {
        this.mainMenu.classList.remove('hidden');
        this.gameArea.classList.add('hidden');

        if (hasSave) {
            this.continueBtn.classList.remove('hidden');
            this.saveInfo.classList.remove('hidden');

            // Show save info
            try {
                const saveData = JSON.parse(localStorage.getItem('officeWorkerSave'));
                const date = new Date(saveData.timestamp);
                this.saveInfo.textContent = `Last saved: ${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
            } catch (e) {
                this.saveInfo.textContent = 'Save data found';
            }
        }
    }

    startGame(isNewGame = true) {
        this.mainMenu.classList.add('hidden');
        this.gameArea.classList.remove('hidden');

        // Show tutorial for new games if not seen before
        if (isNewGame && !localStorage.getItem('tutorialSeen')) {
            this.tutorialPanel.classList.remove('hidden');
        }

        // Force initial render of tasks and upgrades
        // This ensures they display correctly on game start
        this.updateTaskStates();
        this.updateUpgradeStates();
        this.render();
    }

    showSaveIndicator() {
        this.saveIndicator.classList.add('show');
        setTimeout(() => {
            this.saveIndicator.classList.remove('show');
        }, 2000);
    }

    showNotification(message) {
        const notification = document.createElement('div');
        notification.className = 'notification';
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.remove();
        }, 3000);
    }

    getPromotionHints(level) {
        const hints = [];
        const bonusPercent = level * 10;

        // Add bonus hint (always present)
        hints.push(`+${bonusPercent}% money bonus`);

        // Level-specific unlocks
        switch(level) {
            case 1:
                hints.push('Unlocked: Macro Keyboard');
                break;
            case 2:
                hints.push('Unlocked: Scripted Workflow');
                break;
            case 3:
                hints.push('Unlocked: Office Politics');
                break;
            case 4:
                hints.push('Unlocked: AI Assistant');
                break;
        }

        return hints;
    }

    showPromotionNotification(level) {
        const title = this.game.prestige.title;
        const hints = this.getPromotionHints(level);

        const notification = document.createElement('div');
        notification.className = 'notification promotion-notification';
        notification.style.cssText = 'text-align: left; padding: 12px 16px;';

        let html = `<strong>Promoted to ${title}!</strong>`;
        if (hints.length > 0) {
            html += '<br><span style="font-size: 0.9em; opacity: 0.9;">';
            html += hints.join(' • ');
            html += '</span>';
        }

        notification.innerHTML = html;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.remove();
        }, 4000);
    }

    createTaskElements() {
        this.game.tasks.forEach(task => {
            const div = document.createElement('div');
            div.className = 'card task';
            div.innerHTML = `
                <div class="task-content">
                    <div class="task-header">
                        ${task.name} ($${task.reward})
                        <small>${task.description}</small>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-bar-fill"></div>
                    </div>
                </div>
                <button class="task-button">Start</button>
            `;

            const button = div.querySelector('button');
            button.addEventListener('click', () => {
                // Prevent double-clicks while processing
                if (button.disabled) return;
                button.disabled = true;
                this.handleTaskStart(task.id);
                // Re-enable after a short delay (state will be updated in updateTaskStates)
                setTimeout(() => this.updateTaskStates(), 50);
            }, { passive: true });

            this.taskElements.set(task.id, {
                element: div,
                button: button,
                progressBar: div.querySelector('.progress-bar-fill')
            });
        });
    }

    createUpgradeElements() {
        this.game.upgrades.forEach(upgrade => {
            const div = document.createElement('div');
            div.className = 'card upgrade';
            div.innerHTML = `
                <div class="upgrade-content">
                    ${upgrade.name} ($${upgrade.cost})
                    <small>${upgrade.description}</small>
                    ${upgrade.type === 'temporary' ? '<div class="timer-bar"><div class="timer-fill"></div></div>' : ''}
                </div>
                <button class="upgrade-button">Use</button>
            `;

            const button = div.querySelector('button');
            button.addEventListener('click', () => {
                // Prevent double-clicks while processing
                if (button.disabled) return;
                button.disabled = true;
                this.handleUpgradePurchase(upgrade.id);
                // Re-enable after a short delay (state will be updated in updateUpgradeStates)
                setTimeout(() => this.updateUpgradeStates(), 50);
            }, { passive: true });

            this.upgradeElements.set(upgrade.id, {
                element: div,
                button: button,
                timerBar: upgrade.type === 'temporary' ? div.querySelector('.timer-fill') : null
            });
        });
    }

    handleTaskStart(taskId) {
        if (this.game.startTask(taskId)) {
            this.updateTaskStates();
        }
    }

    handleUpgradePurchase(upgradeId) {
        if (this.game.buyUpgrade(upgradeId)) {
            this.updateUpgradeStates();
        }
    }

    render() {
        // Check for game completion
        if (this.game.gameComplete && !this.gameCompleteShown) {
            this.gameCompleteShown = true;
            this.showGameComplete();
            return;
        }

        const newState = this.getGameState();

        // Sync dialogueVisible with game state - recover from stuck states
        // If UI thinks dialogue is visible but game has no active dialogue, reset
        if (this.dialogueVisible && !newState.dialogue) {
            this.dialogueVisible = false;
            this.dialogueBox.classList.add('hidden');
        }

        // Additional recovery: If dialogueVisible is true but the dialogue box is actually hidden in the DOM,
        // reset dialogueVisible. This prevents state desync issues especially on mobile where touch events
        // and browser optimizations can cause race conditions.
        if (this.dialogueVisible && this.dialogueBox.classList.contains('hidden')) {
            this.dialogueVisible = false;
        }

        // Recovery: If there's a queued dialogue but nothing is showing, process it immediately
        // This catches cases where the game loop timing causes missed queue processing
        if (!this.dialogueVisible && !newState.dialogue && this.game.dialogueQueue.length > 0 && this.game.dialogueHandled) {
            this.game.currentDialogue = this.game.dialogueQueue.shift();
            this.game.dialogueHandled = false;
            // Update newState to reflect the change
            newState.dialogue = this.game.currentDialogue;
            newState.dialogueHandled = false;
        }

        // Handle dialogue first
        if (newState.dialogue && !newState.dialogueHandled && !this.dialogueVisible) {
            this.showDialogue(newState.dialogue);
        }

        // Update resources if changed
        if (newState.money !== this.lastGameState.money || 
            newState.reputation !== this.lastGameState.reputation) {
            this.updateResources(newState);
        }

        // Update task visibility and states if changed
        if (!this.arraysEqual(newState.unlockedTasks, this.lastGameState.unlockedTasks) ||
            newState.currentTask !== this.lastGameState.currentTask ||
            newState.workProgress !== this.lastGameState.workProgress) {
            this.updateTaskStates(newState);
        }

        // Update upgrade visibility and button states if changed
        // Also update when money changes (affects button enabled/disabled state)
        if (!this.arraysEqual(newState.unlockedUpgrades, this.lastGameState.unlockedUpgrades) ||
            newState.money !== this.lastGameState.money) {
            this.updateUpgradeStates(newState);
        }

        // Always update work area (for progress updates when task is active)
        if (newState.currentTask) {
            this.updateWorkArea();
        }

        // Update stats panel only when relevant values change
        if (newState.tasksCompleted !== this.lastGameState.tasksCompleted ||
            newState.money !== this.lastGameState.money ||
            newState.reputation !== this.lastGameState.reputation ||
            newState.achievementsCount !== this.lastGameState.achievementsCount ||
            newState.coffeeTaken !== this.lastGameState.coffeeTaken ||
            newState.productivityMultiplier !== this.lastGameState.productivityMultiplier) {
            this.updateStats(newState);
        }

        // Update employees panel only when employee count or task state changes
        if (newState.employeeCount !== this.lastGameState.employeeCount ||
            newState.tasksCompleted !== this.lastGameState.tasksCompleted ||
            newState.money !== this.lastGameState.money) {
            this.updateEmployeesPanel();
        }

        // Update career panel only when prestige or money changes
        if (newState.prestigeLevel !== this.lastGameState.prestigeLevel ||
            newState.money !== this.lastGameState.money ||
            newState.tasksCompleted !== this.lastGameState.tasksCompleted) {
            this.updateCareerPanel();
        }

        // Update politics panel when politics state changes (including dilemmas and timers)
        if (newState.politicsEnabled !== this.lastGameState.politicsEnabled ||
            newState.influence !== this.lastGameState.influence ||
            newState.money !== this.lastGameState.money ||
            newState.currentDilemma !== this.lastGameState.currentDilemma ||
            newState.dilemmaTimer !== this.lastGameState.dilemmaTimer ||
            newState.politicsGameOver !== this.lastGameState.politicsGameOver) {
            this.updatePoliticsPanel();
        }

        // Update automation display only when auto-clicker changes
        if (newState.autoClickerCps !== this.lastGameState.autoClickerCps) {
            this.updateAutomationDisplay();
        }

        // Update automation panel only occasionally (not every frame)
        // Check if any automation features have changed
        this.updateAutomationPanel();

        this.lastGameState = newState;
    }

    updateStats(newState) {
        // Use cached DOM references for performance
        const s = this.statElements;
        const last = this.lastGameState;

        // Only update DOM when values actually change
        if (newState.tasksCompleted !== last.tasksCompleted) {
            s.tasks.textContent = newState.tasksCompleted;
        }
        if (newState.money !== last.money) {
            s.earned.textContent = `$${newState.money}`;
        }
        if (newState.reputation !== last.reputation) {
            s.rep.textContent = newState.reputation;
        }
        if (newState.achievementsCount !== last.achievementsCount) {
            s.achievements.textContent = `${newState.achievementsCount}/${this.game.achievementsList.length}`;
        }
        if (newState.coffeeTaken !== last.coffeeTaken) {
            s.coffee.textContent = newState.coffeeTaken;
        }
        if (newState.productivityMultiplier !== last.productivityMultiplier) {
            s.productivity.textContent = `${Math.round(newState.productivityMultiplier * 100)}%`;
        }
    }

    updateEmployeesPanel() {
        // Show panel after first tasks completed
        if (this.game.stats.tasksCompleted >= 3) {
            this.employeesPanel.classList.remove('hidden');
        }

        // Update hire buttons
        if (!this.game.employeeTypes || !Array.isArray(this.game.employeeTypes)) {
            return;
        }

        // Create hire buttons only once, then update their state
        if (!this.hireButtons) {
            this.hireButtons = new Map();
            this.game.employeeTypes.forEach((type) => {
                if (!type || !type.id) {
                    return;
                }
                const btn = document.createElement('button');
                btn.className = 'hire-btn';
                btn.dataset.typeId = type.id;
                btn.addEventListener('click', () => {
                    const currentCost = this.game.getEmployeeCost(type.id);
                    if (this.game.resources.money < currentCost) {
                        this.showNotification(`Not enough money! Need $${currentCost}`);
                        return;
                    }
                    if (this.game.hireEmployee(type.id)) {
                        this.showNotification(`Hired a new ${type.name}!`);
                        this.updateEmployeesPanel();
                    }
                });
                this.hireButtons.set(type.id, btn);
                this.employeeHireButtons.appendChild(btn);
            });
        }

        // Update button states without recreating them
        this.game.employeeTypes.forEach((type) => {
            if (!type || !type.id) {
                return;
            }
            const btn = this.hireButtons.get(type.id);
            if (!btn) return;

            const cost = this.game.getEmployeeCost(type.id);
            const count = this.game.employees.filter(e => e.type === type.id).length;
            btn.innerHTML = `Hire ${type.name} ($${cost})`;
            btn.disabled = this.game.resources.money < cost;
            btn.title = `${type.description} (${count} hired)`;
        });

        // Update employee list
        if (!this.game.employees || !Array.isArray(this.game.employees)) {
            return;
        }

        // Initialize employee element cache if needed
        if (!this.employeeElements) {
            this.employeeElements = new Map();
        }

        if (this.game.employees.length === 0) {
            // Clear all employee elements
            for (const [, el] of this.employeeElements) {
                if (el.parentNode) el.parentNode.removeChild(el);
            }
            this.employeeElements.clear();

            // Show placeholder only if not already shown
            if (!this.employeeList.querySelector('.no-employees')) {
                this.employeeList.innerHTML = '<small class="no-employees">No employees yet. Hire your first intern!</small>';
            }
        } else {
            // Remove placeholder if present
            const placeholder = this.employeeList.querySelector('.no-employees');
            if (placeholder) placeholder.remove();

            // Track current employee IDs
            const currentIds = new Set(this.game.employees.map(e => e.id));

            // Remove elements for employees that no longer exist
            for (const [id, el] of this.employeeElements) {
                if (!currentIds.has(id)) {
                    if (el.parentNode) el.parentNode.removeChild(el);
                    this.employeeElements.delete(id);
                }
            }

            // Update or create elements for current employees
            this.game.employees.forEach((employee) => {
                if (!employee) return;

                const task = employee.currentTask ?
                    this.game.tasks.find(t => t.id === employee.currentTask) : null;
                const progress = task ?
                    Math.floor((employee.workProgress / task.workNeeded) * 100) : 0;

                let div = this.employeeElements.get(employee.id);

                if (!div) {
                    // Create new element
                    div = document.createElement('div');
                    div.className = 'employee-item';
                    this.employeeElements.set(employee.id, div);
                    this.employeeList.appendChild(div);
                }

                // Update content
                div.innerHTML = `
                    <div>
                        <strong>${employee.name}</strong>
                        <div class="employee-task">
                            ${task ? `Working: ${task.name}` : 'Idle'}
                        </div>
                        ${task ? `<div class="employee-progress"><div class="employee-progress-fill" style="width: ${progress}%"></div></div>` : ''}
                    </div>
                `;
            });
        }
    }

    updateCareerPanel() {
        // Show panel after completing first week intro
        if (this.game.stats.tasksCompleted >= 5) {
            this.careerPanel.classList.remove('hidden');
        }

        // Update title
        this.currentTitleEl.textContent = this.game.prestige.title;

        // Update promotion requirement
        const nextReq = this.game.getNextPromotionRequirement();
        if (nextReq) {
            const current = this.game.resources.money + this.game.stats.tasksCompleted * 10;
            this.promotionReqEl.textContent = `$${current} / $${nextReq}`;
        } else {
            this.promotionReqEl.textContent = 'Max level reached!';
        }

        // Update bonuses
        const bonuses = this.game.prestige.permanentBonuses;
        const bonusText = [];
        if (bonuses.clickMultiplier > 1) bonusText.push(`Click: +${Math.round((bonuses.clickMultiplier - 1) * 100)}%`);
        if (bonuses.moneyMultiplier > 1) bonusText.push(`Money: +${Math.round((bonuses.moneyMultiplier - 1) * 100)}%`);
        if (bonuses.reputationMultiplier > 1) bonusText.push(`Rep: +${Math.round((bonuses.reputationMultiplier - 1) * 100)}%`);
        if (bonuses.employeeEfficiency > 1) bonusText.push(`Team: +${Math.round((bonuses.employeeEfficiency - 1) * 100)}%`);
        this.permanentBonusesEl.textContent = bonusText.length > 0 ? bonusText.join(' | ') : 'No permanent bonuses yet';

        // Update promote button
        this.promoteBtn.disabled = !this.game.canPromote();
        if (this.game.canPromote()) {
            const nextTitle = this.game.promotionTitles.find(p => p.level === this.game.prestige.level + 1);
            this.promoteBtn.textContent = `Get Promoted to ${nextTitle?.title || 'Next Level'}`;
        }
    }

    updatePoliticsPanel() {
        // Check for politics game over (but not if game is complete - CEO victory takes priority)
        if (this.game.politics.gameOver && !this.game.gameComplete) {
            this.politicsGameover.classList.remove('hidden');
            this.gameoverReason.textContent = this.game.politics.gameOverReason;
            return;
        } else if (this.game.gameComplete) {
            // Hide politics gameover if game is complete
            this.politicsGameover.classList.add('hidden');
        }

        // Show panel when politics is enabled (Team Lead+)
        if (this.game.politics.enabled) {
            this.politicsPanel.classList.remove('hidden');
            this.influenceDisplay.classList.remove('hidden');
            this.influenceEl.textContent = Math.floor(this.game.resources.influence);
        }

        // Update factions with favor bars
        this.factionsDisplay.innerHTML = '';
        Object.entries(this.game.politics.factions).forEach(([id, faction]) => {
            const standing = this.game.getFactionStanding(id);
            const favor = faction.favor;
            const div = document.createElement('div');
            div.className = `faction-item ${standing.toLowerCase()}`;

            // Determine bar color class
            let barClass = 'high';
            if (favor < 20) barClass = 'danger';
            else if (favor < 40) barClass = 'low';
            else if (favor < 60) barClass = 'medium';

            div.innerHTML = `
                <span class="faction-name">${faction.name}</span>
                <span class="faction-standing">${standing} (${favor}/100)</span>
                <div class="faction-favor-bar">
                    <div class="faction-favor-fill ${barClass}" style="width: ${favor}%"></div>
                </div>
            `;
            this.factionsDisplay.appendChild(div);
        });

        // Show faction warnings and recovery notifications
        const warnings = this.game.politics.factionWarnings || {};
        const warningNames = Object.keys(warnings).filter(k => warnings[k]);
        let warningEl = document.getElementById('faction-warning');
        if (warningNames.length > 0) {
            if (!warningEl) {
                warningEl = document.createElement('div');
                warningEl.id = 'faction-warning';
                warningEl.className = 'faction-warning';
                this.factionsDisplay.after(warningEl);
            }
            const names = warningNames.map(k => this.game.politics.factions[k].name).join(', ');
            warningEl.textContent = `⚠ WARNING: ${names} favor is dangerously low! Keep it above 20 or your career is over.`;
            warningEl.classList.remove('hidden');
        } else if (warningEl) {
            warningEl.classList.add('hidden');
        }

        // Show recovery notification
        if (this.game.politics.lastRecovery) {
            const recovery = this.game.politics.lastRecovery;
            this.showNotification(`Spent ${recovery.cost} influence to save your career! ${recovery.faction} pulled back from the brink.`);
            this.game.politics.lastRecovery = null;
        }

        // Update dilemma display
        if (this.game.politics.currentDilemma) {
            const dilemma = this.game.politics.currentDilemma;
            this.dilemmaBox.classList.remove('hidden');
            this.dilemmaTimer.classList.add('hidden');
            this.dilemmaText.textContent = dilemma.text;

            // Build choice HTML with effect indicators (Reigns-style)
            const buildChoiceHTML = (choice) => {
                const factionNames = {
                    management: 'Mgmt',
                    workers: 'Workers',
                    board: 'Board'
                };
                let effectsHTML = '';
                if (choice.effects) {
                    const effects = Object.entries(choice.effects)
                        .map(([faction, value]) => {
                            const name = factionNames[faction] || faction;
                            const isPositive = value > 0;
                            const sign = isPositive ? '+' : '';
                            const className = isPositive ? 'positive' : 'negative';
                            return `<span class="effect-indicator ${className}">${name} ${sign}${value}</span>`;
                        })
                        .join('');
                    effectsHTML = `<div class="dilemma-effects">${effects}</div>`;
                }
                return `<span class="dilemma-choice-text">${choice.text}</span>${effectsHTML}`;
            };

            this.dilemmaChoiceA.innerHTML = buildChoiceHTML(dilemma.choiceA);
            this.dilemmaChoiceB.innerHTML = buildChoiceHTML(dilemma.choiceB);
        } else {
            this.dilemmaBox.classList.add('hidden');
            // Show timer until next dilemma
            if (this.game.politics.enabled && !this.game.politics.gameOver) {
                this.dilemmaTimer.classList.remove('hidden');
                this.dilemmaCountdown.textContent = Math.ceil(this.game.politics.dilemmaTimer);
            }
        }

        // Create scheme buttons only once, then update their state
        if (!this.schemeButtons) {
            this.schemeButtons = new Map();
            this.game.politicalSchemes.forEach((scheme) => {
                const div = document.createElement('div');
                div.className = 'scheme-item';
                div.dataset.schemeId = scheme.id;
                div.innerHTML = `
                    <div class="scheme-header">
                        <span class="scheme-name">${scheme.name}</span>
                        <span class="scheme-cost">$${scheme.cost} | +${scheme.influenceGain} influence</span>
                    </div>
                    <div class="scheme-description">${scheme.description}</div>
                    <button class="scheme-btn">Execute</button>
                    <small class="scheme-completed hidden">Completed</small>
                `;
                const btn = div.querySelector('.scheme-btn');
                btn.addEventListener('click', () => {
                    // Prevent double-clicks
                    if (btn.disabled) return;
                    btn.disabled = true;

                    if (this.game.startScheme(scheme.id)) {
                        this.showNotification(`Completed: ${scheme.name}`);
                    }
                    // Re-enable button after a short delay (state will update on next render)
                    setTimeout(() => {
                        this.updateSchemeButtonStates();
                    }, 100);
                });
                this.schemeButtons.set(scheme.id, { element: div, button: btn });
                this.schemesList.appendChild(div);
            });
        }

        // Update scheme button states
        this.updateSchemeButtonStates();
    }

    updateSchemeButtonStates() {
        if (!this.schemeButtons) return;

        this.game.politicalSchemes.forEach((scheme) => {
            const schemeUI = this.schemeButtons.get(scheme.id);
            if (!schemeUI) return;

            const completed = this.game.politics.completedSchemes.includes(scheme.id);
            const canStart = this.game.canStartScheme(scheme.id);

            schemeUI.element.classList.toggle('completed', completed);
            schemeUI.button.classList.toggle('hidden', completed);
            schemeUI.button.disabled = !canStart;

            const completedLabel = schemeUI.element.querySelector('.scheme-completed');
            if (completedLabel) {
                completedLabel.classList.toggle('hidden', !completed);
            }
        });
    }

    updateAutomationDisplay() {
        // Show auto-clicker display if enabled
        if (this.game.autoClicker.enabled) {
            this.autoClickerDisplay.classList.remove('hidden');
            this.autoClicksEl.textContent = this.game.autoClicker.clicksPerSecond.toFixed(1);
        }
    }

    setupAutomationControls() {
        // Create toggle elements for each automation feature
        this.automationToggles.innerHTML = `
            <div class="automation-toggle" id="toggle-auto-clicker" style="display: none;">
                <label class="toggle-label">
                    <span class="toggle-name">Auto-Clicker</span>
                    <button class="toggle-btn active" data-feature="autoClicker">ON</button>
                </label>
            </div>
            <div class="automation-toggle" id="toggle-email-auto" style="display: none;">
                <label class="toggle-label">
                    <span class="toggle-name">Email Auto-Start</span>
                    <button class="toggle-btn active" data-feature="emailAuto">ON</button>
                </label>
            </div>
            <div class="automation-toggle" id="toggle-task-scheduler" style="display: none;">
                <label class="toggle-label">
                    <span class="toggle-name">Task Scheduler</span>
                    <button class="toggle-btn active" data-feature="taskScheduler">ON</button>
                </label>
            </div>
        `;

        // Add click handlers
        this.automationToggles.querySelectorAll('.toggle-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const feature = btn.dataset.feature;
                let isActive;

                if (feature === 'autoClicker') {
                    isActive = this.game.toggleAutoClicker();
                } else if (feature === 'emailAuto') {
                    isActive = this.game.toggleEmailAutoResponder();
                } else if (feature === 'taskScheduler') {
                    isActive = this.game.toggleTaskScheduler();
                }

                btn.textContent = isActive ? 'ON' : 'OFF';
                btn.classList.toggle('active', isActive);
            });
        });
    }

    updateAutomationPanel() {
        const hasAutoClicker = this.game.autoClicker.enabled;
        const hasEmailAuto = this.game.emailAutoResponder.enabled;
        const hasTaskScheduler = this.game.upgrades.find(u => u.id === 'task_scheduler' && u.isPurchased);

        // Show/hide the panel based on whether any automation features are available
        const hasAnyAutomation = hasAutoClicker || hasEmailAuto || hasTaskScheduler;
        this.automationPanel.classList.toggle('hidden', !hasAnyAutomation);

        if (!hasAnyAutomation) return;

        // Update individual toggle visibility and state
        const autoClickerToggle = document.getElementById('toggle-auto-clicker');
        const emailAutoToggle = document.getElementById('toggle-email-auto');
        const taskSchedulerToggle = document.getElementById('toggle-task-scheduler');

        if (autoClickerToggle) {
            autoClickerToggle.style.display = hasAutoClicker ? 'block' : 'none';
            const btn = autoClickerToggle.querySelector('.toggle-btn');
            if (btn) {
                btn.textContent = this.game.autoClicker.active ? 'ON' : 'OFF';
                btn.classList.toggle('active', this.game.autoClicker.active);
            }
        }

        if (emailAutoToggle) {
            emailAutoToggle.style.display = hasEmailAuto ? 'block' : 'none';
            const btn = emailAutoToggle.querySelector('.toggle-btn');
            if (btn) {
                btn.textContent = this.game.emailAutoResponder.active ? 'ON' : 'OFF';
                btn.classList.toggle('active', this.game.emailAutoResponder.active);
            }
        }

        if (taskSchedulerToggle) {
            taskSchedulerToggle.style.display = hasTaskScheduler ? 'block' : 'none';
            const btn = taskSchedulerToggle.querySelector('.toggle-btn');
            if (btn) {
                btn.textContent = this.game.taskSchedulerActive ? 'ON' : 'OFF';
                btn.classList.toggle('active', this.game.taskSchedulerActive);
            }
        }
    }

    updateTaskStates(state = this.getGameState()) {
        // Track which tasks should be visible
        const visibleTasks = new Set(state.unlockedTasks);

        // Remove tasks that are no longer visible
        if (!this.currentVisibleTasks) {
            this.currentVisibleTasks = new Set();
        }

        // Remove elements that shouldn't be visible anymore
        for (const taskId of this.currentVisibleTasks) {
            if (!visibleTasks.has(taskId)) {
                const taskUI = this.taskElements.get(taskId);
                if (taskUI && taskUI.element.parentNode === this.taskContainer) {
                    this.taskContainer.removeChild(taskUI.element);
                }
            }
        }

        // Add/update visible tasks
        state.unlockedTasks.forEach(taskId => {
            const taskUI = this.taskElements.get(taskId);
            if (!taskUI) return;

            const isActive = this.game.currentTask?.id === taskId;

            // Update button state
            taskUI.button.disabled = isActive;
            taskUI.button.textContent = isActive ? 'In Progress' : 'Start';
            taskUI.element.classList.toggle('active', isActive);

            // Update progress if active
            if (isActive) {
                const progress = (this.game.resources.workInProgress / this.game.currentTask.workNeeded) * 100;
                taskUI.progressBar.style.width = `${Math.min(100, progress)}%`;
            } else {
                taskUI.progressBar.style.width = '0%';
            }

            // Only append if not already in container
            if (taskUI.element.parentNode !== this.taskContainer) {
                this.taskContainer.appendChild(taskUI.element);
            }
        });

        // Update tracked visible tasks
        this.currentVisibleTasks = visibleTasks;
    }

    updateResources(state = this.getGameState()) {
        // Use cached DOM references
        this.statElements.money.textContent = state.money;
        this.statElements.repHeader.textContent = state.reputation;
    }

    showDialogue(dialogue) {
        if (!dialogue || this.dialogueVisible) return;

        const dialogueBox = this.dialogueBox;
        const characterName = dialogueBox.querySelector('.character-name');
        const dialogueText = dialogueBox.querySelector('.dialogue-text');
        const choicesDiv = dialogueBox.querySelector('.dialogue-choices');

        characterName.textContent = dialogue.character;
        dialogueText.textContent = dialogue.text;
        
        choicesDiv.innerHTML = '';
        
        dialogue.choices.forEach((choice, index) => {
            const button = document.createElement('button');
            button.className = 'choice-btn';
            button.textContent = choice.text;

            // Use touch handler for better mobile support
            let handled = false;
            // Capture dialogue ID before it gets cleared by makeDialogueChoice
            const dialogueId = dialogue.id;
            const handleChoice = () => {
                // Prevent double-clicks while processing
                if (button.disabled || handled) return;
                button.disabled = true;
                handled = true;

                // Process the choice
                this.game.makeDialogueChoice(index);

                // Hide dialogue box and reset state
                dialogueBox.classList.add('hidden');
                this.dialogueVisible = false;

                // Defensive check: If this was the CEO victory dialog, ensure game is marked complete
                // This handles edge cases where makeDialogueChoice might not have set gameComplete
                if (dialogueId === 'ceo_victory' && !this.game.gameComplete) {
                    this.game.gameComplete = true;
                }

                // Check for game completion immediately after dialogue choice
                // This ensures the CEO victory ending shows without relying on render loop timing
                if (this.game.gameComplete && !this.gameCompleteShown) {
                    this.gameCompleteShown = true;
                    this.showGameComplete();
                    return;
                }

                // Force immediate queue processing if there's a pending dialogue
                // This prevents race conditions between UI and game loop
                if (this.game.dialogueQueue.length > 0 && !this.game.currentDialogue) {
                    this.game.currentDialogue = this.game.dialogueQueue.shift();
                    this.game.dialogueHandled = false;
                }
            };

            // Touch handler for mobile - fires immediately without 300ms delay
            button.addEventListener('touchend', (e) => {
                e.preventDefault();
                handleChoice();
            }, { passive: false, once: true });

            // Click handler for desktop
            button.addEventListener('click', handleChoice, { passive: true, once: true });

            choicesDiv.appendChild(button);
        });

        dialogueBox.classList.remove('hidden');
        this.dialogueVisible = true;

        // Ensure dialogue is visible on mobile by scrolling to it
        // Use setTimeout to allow DOM update before scrolling
        setTimeout(() => {
            // Scroll to top of page to ensure dialogue is visible
            window.scrollTo({ top: 0, behavior: 'instant' });
            // Also scroll the dialogue box into view as a fallback
            dialogueBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    }

    updateUpgradeStates() {
        // Track which upgrades should be visible
        const visibleUpgrades = new Set();

        this.game.upgrades.forEach(upgrade => {
            if (!upgrade.locked) {
                visibleUpgrades.add(upgrade.id);
            }
        });

        // Remove elements that shouldn't be visible anymore
        if (!this.currentVisibleUpgrades) {
            this.currentVisibleUpgrades = new Set();
        }

        for (const upgradeId of this.currentVisibleUpgrades) {
            if (!visibleUpgrades.has(upgradeId)) {
                const upgradeUI = this.upgradeElements.get(upgradeId);
                if (upgradeUI && upgradeUI.element.parentNode === this.upgradeContainer) {
                    this.upgradeContainer.removeChild(upgradeUI.element);
                }
            }
        }

        // Add/update visible upgrades
        this.game.upgrades.forEach(upgrade => {
            if (!upgrade.locked) {
                const upgradeUI = this.upgradeElements.get(upgrade.id);
                if (upgradeUI) {
                    const button = upgradeUI.button;
                    button.disabled = upgrade.isPurchased || this.game.resources.money < upgrade.cost;

                    if (upgrade.type === 'temporary' && upgrade.timeLeft > 0) {
                        upgradeUI.timerBar.style.width = `${(upgrade.timeLeft / upgrade.duration) * 100}%`;
                    }

                    // Only append if not already in container
                    if (upgradeUI.element.parentNode !== this.upgradeContainer) {
                        this.upgradeContainer.appendChild(upgradeUI.element);
                    }
                }
            }
        });

        // Update tracked visible upgrades
        this.currentVisibleUpgrades = visibleUpgrades;
    }

    updateWorkArea() {
        if (this.game.currentTask) {
            this.workArea.classList.remove('hidden');
            this.currentTaskName.textContent = `Working on: ${this.game.currentTask.name}`;
            const progress = (this.game.resources.workInProgress / this.game.currentTask.workNeeded) * 100;
            this.workProgressFill.style.width = `${Math.min(100, progress)}%`;
            this.workProgressText.textContent = `${Math.floor(progress)}%`;
        } else {
            this.workArea.classList.add('hidden');
        }
    }

    showGameComplete() {
        // Check if this is a CEO victory
        const isCeoVictory = this.game.prestige.level >= 10;

        // Update the title and message based on victory type
        const completeContent = this.gameCompleteScreen.querySelector('.complete-content');
        const titleEl = completeContent.querySelector('h2');
        const messageEl = completeContent.querySelector('p');

        if (isCeoVictory) {
            titleEl.textContent = 'You Are The CEO!';
            messageEl.textContent = 'From humble New Hire to Chief Executive Officer - your journey to the top is complete!';
        } else {
            titleEl.textContent = 'Congratulations!';
            messageEl.textContent = "You've completed your first week at the office.";
        }

        // Populate final stats
        document.getElementById('final-money').textContent = `Money earned: $${this.game.resources.money}`;
        document.getElementById('final-reputation').textContent = `Reputation: ${this.game.resources.reputation}`;
        document.getElementById('final-tasks').textContent = `Tasks completed: ${this.game.stats.tasksCompleted}`;
        document.getElementById('final-achievements').textContent = `Achievements: ${this.game.achievements.size}/${this.game.achievementsList.length}`;

        // Add title info for CEO victory
        let titleInfo = document.getElementById('final-title');
        if (!titleInfo) {
            titleInfo = document.createElement('div');
            titleInfo.id = 'final-title';
            document.getElementById('final-achievements').after(titleInfo);
        }
        titleInfo.textContent = `Final Title: ${this.game.prestige.title}`;

        // Add political journey stats for CEO victory
        if (isCeoVictory && this.game.politics.enabled) {
            let politicsInfo = document.getElementById('final-politics');
            if (!politicsInfo) {
                politicsInfo = document.createElement('div');
                politicsInfo.id = 'final-politics';
                politicsInfo.className = 'final-politics-section';
                titleInfo.after(politicsInfo);
            }
            const factions = this.game.politics.factions;
            const dilemmasResolved = this.game.politics.completedDilemmas.length;
            const schemesCompleted = this.game.politics.completedSchemes.length;
            const rounds = this.game.politics.dilemmaRound || 0;
            let politicsHTML = '<h3>Political Legacy</h3>';
            politicsHTML += `<div>Influence: ${Math.floor(this.game.resources.influence)}</div>`;
            politicsHTML += `<div>Dilemmas resolved: ${dilemmasResolved}${rounds > 0 ? ` (${rounds + 1} rounds)` : ''}</div>`;
            politicsHTML += `<div>Schemes completed: ${schemesCompleted}/${this.game.politicalSchemes.length}</div>`;
            politicsHTML += `<div>Power plays: ${this.game.politics.powerPlays}</div>`;
            Object.entries(factions).forEach(([, faction]) => {
                const standing = this.game.getFactionStanding(Object.keys(factions).find(k => factions[k] === faction));
                politicsHTML += `<div>${faction.name}: ${standing} (${faction.favor}/100)</div>`;
            });
            politicsInfo.innerHTML = politicsHTML;
        }

        // Populate relationships
        const relDiv = document.getElementById('final-relationships');
        let relHTML = '';
        const formatRel = (value) => value > 0 ? `+${value}` : value < 0 ? `${value}` : '0';
        relHTML += `<div>Sarah (HR): ${formatRel(this.game.relationships.hrManager)}</div>`;
        relHTML += `<div>Mark (Supervisor): ${formatRel(this.game.relationships.supervisor)}</div>`;

        Object.entries(this.game.relationships.colleagues).forEach(([name, value]) => {
            const displayName = name.charAt(0).toUpperCase() + name.slice(1);
            relHTML += `<div>${displayName}: ${formatRel(value)}</div>`;
        });

        relDiv.innerHTML = relHTML;

        // Hide the game area and show the completion screen
        document.getElementById('game-area').classList.add('hidden');
        this.gameCompleteScreen.classList.remove('hidden');

        // Also hide politics gameover if it was showing (CEO victory takes priority)
        this.politicsGameover.classList.add('hidden');

        // Ensure game complete screen is visible on mobile
        // Scroll to top and force visibility
        window.scrollTo({ top: 0, behavior: 'instant' });

        // Focus the play again button for accessibility and to ensure visibility
        setTimeout(() => {
            this.playAgainBtn.focus();
        }, 100);
    }

    update() {
        // Call render directly - main.js already uses requestAnimationFrame
        this.render();
    }
}

export { GameUI }; 